import type { ReactNode } from 'react';
import { getCategoriesForLabel, resolveLabelDisplayName } from '../../lib/clinic-logic';
import { cn } from '../../lib/utils';
import type { EmailThreadMetadata, LabelCategoryMapping, TriageCategory } from '../../types/email';
import { Badge } from '../../ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../ui/tooltip';

export interface LabelBadgeListProps {
  thread: EmailThreadMetadata;
  /** Raw label ids to render, already filtered/ordered by the caller (e.g. star handling). */
  labels: string[];
  labelMap?: Record<string, string>;
  categories?: TriageCategory[];
  mappings?: LabelCategoryMapping[];
  maxVisible: number;
  showCategoryDots?: boolean;
  /** Full className for each visible badge; defaults to the dense-row look if omitted. */
  badgeClassName?: string;
  /** Override for a label's display text, e.g. rendering a special label as an icon. */
  renderLabel?: (labelId: string) => ReactNode;
}

const DEFAULT_BADGE_CLASSNAME =
  'h-5 max-w-[100px] truncate border-none bg-muted px-1.5 text-[10px] gap-1';

function CategoryDots({ cats }: { cats: TriageCategory[] }) {
  if (cats.length === 0) return null;
  return (
    <div className="flex gap-0.5">
      {cats.map((cat) => (
        <div
          key={cat.id}
          className="h-1.5 w-1.5 shrink-0 rounded-full"
          style={{ backgroundColor: cat.color }}
          title={cat.name}
        />
      ))}
    </div>
  );
}

/**
 * Shared label-badge renderer for the Triage row/card views. Owns the
 * label→category lookup (via clinic-logic.ts, so the composite/flat mail-tags
 * key rule lives in exactly one place) and the truncation/overflow behavior,
 * so a truncated label list never silently drops its category signal — the
 * overflow badge's tooltip carries the hidden labels and their categories
 * instead of losing them.
 */
export function LabelBadgeList({
  thread,
  labels,
  labelMap = {},
  categories = [],
  mappings = [],
  maxVisible,
  showCategoryDots = true,
  badgeClassName,
  renderLabel,
}: LabelBadgeListProps) {
  const visible = labels.slice(0, maxVisible);
  const hidden = labels.slice(maxVisible);

  const displayFor = (labelId: string): ReactNode =>
    renderLabel ? renderLabel(labelId) : resolveLabelDisplayName(labelMap, thread, labelId);

  return (
    <>
      {visible.map((l) => {
        const cats = showCategoryDots ? getCategoriesForLabel(thread, mappings, categories, l) : [];
        return (
          <Badge
            key={l}
            variant="outline"
            className={cn(badgeClassName || DEFAULT_BADGE_CLASSNAME)}
            title={resolveLabelDisplayName(labelMap, thread, l)}
          >
            {displayFor(l)}
            <CategoryDots cats={cats} />
          </Badge>
        );
      })}
      {hidden.length > 0 && (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="text-[10px] text-muted-foreground hover:text-foreground"
            >
              +{hidden.length}
            </button>
          </TooltipTrigger>
          <TooltipContent>
            <div className="space-y-1">
              {hidden.map((l) => {
                const cats = showCategoryDots
                  ? getCategoriesForLabel(thread, mappings, categories, l)
                  : [];
                return (
                  <div key={l} className="flex items-center gap-1.5">
                    <span>{displayFor(l)}</span>
                    <CategoryDots cats={cats} />
                  </div>
                );
              })}
            </div>
          </TooltipContent>
        </Tooltip>
      )}
    </>
  );
}
